<template>
	<div style='width: 100%;'>
		<div class="py-4 mb-4">
			<div class="session-card__wrapper mb-2" style="padding: 0;">
				<div v-if='agendaEvent.sponsored_by' class="session-card__pin">
					<span>
						<img 
							:src='sponsoredStarIcon' 
							alt='iconSponsored' 
							class='mr-1' 
						/>
						{{ sponsoredLabel }}
					</span>
				</div>
				<!-- text -->
				<div
					v-if='agendaEvent.events_agenda_text'
					class="session-card-description mb-4"
				>
					{{ agendaEvent.events_agenda_text }}
				</div>
	
				<!-- date time -->
				<div
					v-if='agendaEvent.events_agenda_from && agendaEvent.events_agenda_to'
					class="session-card-date mb-2"
				>
					{{ formatDateRange(agendaEvent.events_agenda_from, agendaEvent.events_agenda_to, agendaEvent.agendaTimezone) }}
				</div>
	
				<!-- location -->
				<div class="session-card-location mb-3" v-if="agendaEvent.session_location">
					<Icon 
						type="pin"
						:size="10"
						color="var(--button-main-background-color)"
						:pr="8"
					/>
					{{ agendaEvent.session_location }}
				</div>

	
				<!-- tracks -->
				<div class='mb-4' v-if='agendaEvent.tracks.length > 0' style="padding: 0 30px;">
					<div
						v-for="{ track_color, track_id, track_name } in agendaEvent.tracks"
						:key="track_id"
						class="session-card-tag mb-1"
						:style="`color: ${track_color}`"
					>
						<v-icon :color="track_color" dark small class="mr-1"> mdi-circle</v-icon>
						{{ track_name }}
					</div>
				</div>

				<div class="session-card-registered mb-4" v-if="agendaEvent.is_registered">
					<v-chip class="session-card-registered-chip">
						Registered
					</v-chip>
				</div>

				<!-- description -->
				<div
					class="session-card-text mb-4"
					v-if='agendaEvent.session_description'
					v-html='agendaEvent.session_description'
				/>

				<v-divider />
				<div class="session-card-add-my-agenda-wrapper">
					<v-btn 
						class="session-card-add-my-agenda-btn elevation-0" 
						v-bind:style="{color: myAgendaButton.color}"
						@click="toggleMyAgenda"
						:loading="isMyAgendaLoading"
					>
						<Icon 
							:type="myAgendaButton.icon"
							:size="24"
							:color="myAgendaButton.color"
							:pr="8"
						/>
						<span class="session-card-add-my-agenda-btn-text">
							{{ myAgendaButton.text }}
						</span>
					</v-btn>
				</div>
			</div>
			<div class="session-card__wrapper">
				<!-- speakers -->
	
				<h4
					v-if='agendaEvent.speakers.length > 0'
					class='mb-3'
				>
					Speaker(s)
				</h4>
	
				<div v-if='agendaEvent.speakers.length > 0'>
					<router-link
						:to="{
							name: 'speaker-by-id',
							params: {
								id: +speaker_id
							}
						}"
						v-for="{ speaker_logo, speaker_name, speaker_title, speaker_id } in agendaEvent.speakers.slice(0,3)"
						:key="speaker_id"
						class="attend-card-wrapper text-decoration-none d-flex align-center px-8 py-6 mb-4"
					>
						<Avatar
							class="mr-8 post-preview-card__avatar"
							ize="85"
							:avatar="speaker_logo"
						/>
						<div>
							<div
								v-if='speaker_name'
								class="attend-card-title mb-2"
							>
								{{ speaker_name }}
							</div>
							<div
								v-if='speaker_title'
								class="attend-card-location mb-2"
							>
								{{ speaker_title }}
							</div>
						</div>
					</router-link>
				</div>
	
				<div 
					class='session-card__button mt-3 text-center' 
					v-if='agendaEvent.speakers.length >= 3'
				>
					<router-link
						:to="{
							name: 'event-session-speakers',
							params: {
								eventId: agendaEvent.events_id,
								sessionId: agendaEvent.events_agenda_id
							}
						}"
						class='session-card__button d-flex text-decoration-none text-center align-center'
					>
						<span class='ma-auto'>Show All</span>
					</router-link>
				</div>
	
				<!-- files -->
				<h4 class='mb-3' v-if='agendaEvent.uploads.length > 0'>File(s)</h4>
	
				<div v-if="agendaEvent.uploads && agendaEvent.uploads.length" class="mt-5">
					<div
						class="d-flex flex-column flex-wrap w-full pb-4 mb-n2"
						v-for="attachment in agendaEvent.uploads.slice(0,3)"
						:key="attachment.upload_id"
					>
						<SessionAttachments :attachment="attachment" />
					</div>
				</div>
	
				<div class='session-card__button mt-3 text-center' v-if='agendaEvent.uploads.length >= 3'>
					<router-link
						:to="{
							name: 'event-session-files',
							params: {
								eventId: this.$route.params.eventId,
								sessionId: this.$route.params.sessionId
							}
						}"
						class="session-card__button text-decoration-none"
					>
						<span>Show All</span>
					</router-link>
				</div>
	
				<!-- questions -->
				<div v-if="agendaEvent.session_qa_is_available">
					<h4 class='mb-3'>Top 3 Session Question(s)</h4>
					<div v-if="agendaEvent.session_question_answers.length">
						<div 
							v-for="question in agendaEvent.session_question_answers" 
							:key="question.session_question_answer_id"
							class="attend-card-wrapper text-decoration-none d-flex align-center justify-space-between px-8 py-6 mb-4"
							:class="question.upvote ? 'session-card__question-upvote' : ''"
						>
							<span class="session-card__question text-truncate mr-2">
								{{ question.session_question_answer_text }}
							</span>
							<span class="session-card__question-count d-flex align-center">
								<Icon 
									type="like"
									:size="16"
									color="#697583"
									:pr="4"
								/>
								{{ question.session_question_count }}
							</span>
						</div>
					</div>
					<div class="d-flex justify-center">
						<router-link 
							:to="{name: 'event-session-questions'}" 
						>
							<v-btn v-if="!agendaEvent.session_question_answers.length" class="session-card__add-question-btn elevation-0">
								Add a Question
							</v-btn>
							<v-btn v-else class="session-card__add-question-btn-view elevation-0">
								View More/Add
							</v-btn>
						</router-link>
					</div>
				</div>
				<div v-if="polls.length">
					<h4 class='mb-3'>Survey(s)</h4>
					<Poll 
						v-for="poll in polls" 
						:key="poll.poll_id" 
						:poll="poll" 
						:answers="polls_answers"
						:isEventVoteDirty="isVoteDirty"
						:pollsLoading="pollsLoading"
						@answerChange="answerChange"
					/>
					<v-btn 
						v-if="!isPollsVoted"
						class="event-card__poll-btn"  
						:class="{'event-card__poll-btn-disabled': voteDisabled}"
						:ripple="false"
						@click="handleVote"
						:loading="pollsLoading"
					>
						Vote
					</v-btn>
				</div>
				<div v-if="ratings.length" class="mt-6">
					<h4 class='mb-3'>Rating(s)</h4>
					<Rating 
						v-for="rating in ratings"
						:key="rating.rating_id"
						:rating="rating"
						@ratingChange="ratingChange"
					/>
					<v-btn
						v-if="isRatingsAvailable"
						class="event-card__rating-btn"
						:class="{'event-card__rating-btn-disabled': ratingDisabled}"
						:ripple="false"
						@click="handleRate"
						:loading="ratingsLoading"
					>
						Send Rating
					</v-btn>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import Vue from 'vue'
import VueScrollTo from 'vue-scrollto'
import Avatar from '@/components/Avatar.vue'
import SessionAttachments from '@/components/Resource/SessionAttachments.vue'
import sponsoredStarIcon from '@/assets/images/icoт-sponsored-star.svg'
import Poll from '@/components/Post/Poll'
import Rating from '@/components/Event/Rating'
import { mapActions, mapGetters } from 'vuex'

Vue.use(VueScrollTo)

export default {
	name: 'SessionCard',
	components: { 
		SessionAttachments, 
		Avatar,
		Poll,
		Rating
	},
	props: {
		agendaEvent: {
			type: Object,
			default: () => {},
		},
	},
	data() {
		return {
			sponsoredStarIcon,
			polls_answers: [],
			isVoteDirty: false,
			rating_answers: []
		}
	},

	methods: {
		...mapActions('event', ['addMyAgenda', 'removeMyAgenda', 'voteEventPolls', 'voteSessionRatings']),

		formatDateRange(dateFrom, dateTo, timezone) {
			const options = {
				weekday: 'long',
				hour: '2-digit',
				minute: '2-digit',
				hour12: true,
				timeZone: 'UTC'
			}

			//Convert Unix timestamps to JavaScript Date objects
			const fromDate = new Date(dateFrom * 1000)
			const toDate = new Date(dateTo * 1000)
			const timezoneOffset = Number(timezone.timezone_utc.slice(-2))
			const action = timezone.timezone_utc.slice(3, 4)
			if (action === '-') {
				fromDate.setHours(fromDate.getHours() - timezoneOffset)
				toDate.setHours(toDate.getHours() - timezoneOffset)
			} else {
				fromDate.setHours(fromDate.getHours() + timezoneOffset)
				toDate.setHours(toDate.getHours() + timezoneOffset)
			}

			// Format the dates
			const formattedFromDate = fromDate.toLocaleString('en-US', options)
			const formattedToDate = toDate.toLocaleTimeString('en-US', options)

			// Construct the formatted date range string
			return `${formattedFromDate} - ${formattedToDate}`
		},
		toggleMyAgenda() {
			if (!this.isMyAgendaLoading) {
				if (this.agendaEvent.is_scheduled) {
					this.removeMyAgenda({ id: this.agendaEvent.events_agenda_id, myAgenda: false, single: true })
				} else {
					this.addMyAgenda({ id: this.agendaEvent.events_agenda_id, single: true })
				}
			}
		},
		answerChange(answer) {
			if (!answer.answer || (Array.isArray(answer.answer) && !answer.answer.length)) {
				if (this.polls_answers.length) {
					this.polls_answers = this.polls_answers.filter(poll => poll.poll_id !== answer.poll_id)
				}
			} else {
				const pollAnswer = this.polls_answers.find(poll => poll.poll_id === answer.poll_id)
				if (pollAnswer) {
					this.polls_answers = this.polls_answers.map(poll => {
						if (poll.poll_id === answer.poll_id) {
							return answer
						} else {
							return poll
						}
					})
				} else {
					this.polls_answers = [...this.polls_answers, answer]
				}
			}
		},
		async handleVote() {
			this.isVoteDirty = true
			if (!this.voteDisabled) {
				await this.voteEventPolls({ answers: this.polls_answers, eventId: null, sessionId: this.agendaEvent.events_agenda_id })
			}
		},
		ratingChange(rating) {
			if (this.rating_answers.find(item => item.rating_id === rating.rating_id)) {
				this.rating_answers = this.rating_answers.map(item => {
					if (item.rating_id === rating.rating_id) {
						return {
							...rating
						}
					} else {
						return item
					}
				})
			} else {
				this.rating_answers = [...this.rating_answers, rating]
			}
		},
		async handleRate() {
			if (!this.ratingDisabled) {
				await this.voteSessionRatings({ ratings: this.rating_answers, sessionId: +this.$route.params.sessionId })
			}
		}
	},
	computed: {
		...mapGetters('event', ['isMyAgendaLoading', 'pollsLoading', 'ratingsLoading']),

		polls() {
			return this.agendaEvent.polls.filter(poll => poll.pollsAnswers.length || (!poll.pollsAnswers.length && poll.allow_custom_answer))
		},
		voteDisabled() {
			return this.pollsLoading || this.polls_answers.length !== this.polls.length || this.polls_answers.some(poll => poll.error)
		},
		isPollsVoted() {
			return this.agendaEvent.polls.every(poll => poll.vote)
		},
		sponsoredLabel() {
			return this.agendaEvent.sponsored_by ? `Sponsored by ${this.agendaEvent.sponsored_by}` : ''
		},
		myAgendaButton() {
			return this.agendaEvent.is_scheduled ? 
			{text: 'Remove from My Agenda', color: '#9B9B9B', icon: 'calendar_check'} 
			: 
			{text: 'Add to My Agenda', color: 'var(--accent-color)', icon: 'calendar_star'}
		},
		ratings() {
			return this.agendaEvent.ratings.filter(rating => !rating.vote || (rating.vote && rating.display_rating))
		},
		isRatingsAvailable() {
			return this.ratings.some(rating => !rating.vote)
		},
		availableRatings() {
			return this.ratings.filter(rating => !rating.vote)
		},
		ratingDisabled() {
			return this.rating_answers.length !== this.availableRatings.length
		}
	}
}
</script>

<style lang="scss">
.session-card {
	&__wrapper {
		position: relative;
		background-color: #fff;
		padding: 30px;
		border-radius: 10px;
	}

	&__pin {
		text-align: right;
		position: absolute;
		top: 0;
		right: 0;

		span {
			z-index: 0;
			position: relative;
			background: var(--badge-sponsored-background);
			display: inline-flex;
			padding: 5px 10px 5px 10px;
			border-top-right-radius: 8px;
			color: rgb(255, 255, 255);
			font-size: 12px;
			font-family: Montserrat, sans-serif;
			font-weight: bold;
		}

		// Start
		span {
			&:before {
				z-index: -1;
				content: '';
				position: absolute;
				left: -8%;
				top: 0;
				height: 100%;
				width: 100%;
				background: var(--badge-sponsored-background);
				-webkit-transform: skewX(20deg);
				-moz-transform: skewX(20deg);
				-ms-transform: skewX(20deg);
				transform: skewX(20deg);
			}
		}
	}
	&-date {
		color: var(--accent-color);
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		font-weight: 700;
		line-height: 17.07px;
		padding: 0 30px;
	}

	&-description {
		color: var(--accent-color);
		font-size: 16px;
		font-family: Montserrat, sans-serif;
		font-weight: 700;
		line-height: 19.5px;
		padding: 30px 30px 0 30px;
	}

	&-text {
		color: var(--accent-color);
		font-size: 14px;
		font-family: Montserrat, sans-serif;
		line-height: 17.07px;
		padding: 0 30px;

		p {
			margin: 0;
		}
	}

	&-tag {
		display: flex;
		align-items: center;
		font-size: 13px;
		font-family: Montserrat, sans-serif;
		font-weight: 700;
		line-height: 19.37px;
	}

	&-location {
		display: flex;
		align-items: center;
		color: var(--button-main-background-color);
		font-size: 13px;
		font-family: Montserrat, sans-serif;
		font-weight: 500;
		line-height: 19.37px;
		padding: 0 30px;
	}

	&-registered {
		padding: 0 30px;

		&-chip {
			height: 23px !important;
			font-size: 12px !important;
			line-height: 14.63px;
			font-weight: 700;
			font-family: Montserrat, sans-serif;
			background-color: var(--accent-color) !important;
			color: #fff !important;
		}
	}

	&-add-my-agenda {
		&-wrapper {
			padding: 20px 22px;
			display: flex;
			justify-content: space-between;
			flex-wrap: wrap;
		}

		&-btn {
			text-transform: none;
			font-size: 14px;
			font-weight: 700;
			font-family: Montserrat, sans-serif;
			line-height: 17.07px;
			background-color: transparent !important;
			padding: 0 8px !important;

			&-text {
				text-decoration: underline;
			}
		}
	}

	&__add-question-btn, &__add-question-btn-view {
		background: var(--button-main-background-color) !important;
		color: var(--button-main-color) !important;
		text-transform: capitalize;
		font-size: 16px !important;
		font-weight: 700;
		font-family: Montserrat, sans-serif;
	}

	&__add-question-btn-view {
		color: var(--accent-color) !important;
		background: transparent !important;
	}

	&__question {
		font-size: 14px;
		font-weight: 700;
		font-family: Montserrat, sans-serif;
		color: #697583;

		&-upvote {
			.v-icon, span {
				color: #0055A3;
			}
		}
	}

	&-count {
        font-size: 14px;
        font-weight: 700;
        font-family: Montserrat, sans-serif;
        color: #697583;
    }

	&__button {
		font-size: 16px;
		font-weight: 700;
		font-family: Montserrat, sans-serif;
		color: var(--accent-color) !important; 
		text-decoration: underline;
	}
}
</style>
